import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import PointsAllocation from "../components/PointsAllocation";
import EventSelector from "../components/EventSelector";
import PlayerList from "../components/PlayerList";
import { labelMapping } from "../components/PointsAllocation";
import { calculateTotalPoints, MAJOR_MULTIPLIER } from "../utils/pointsUtils";
import calculateQuota from "../utils/calculateQuota";
import Modal from "../components/Modal";
import apiClient from '../utils/apiClient';
import Footer from "../components/Footer";
import Swal from 'sweetalert2';
import { calculateMoneyWonAndPot } from '../utils/moneyUtils';
import { CloudRain, CheckCircle, ChevronLeft, ChevronRight, Sun, Moon, Edit3, Lock, ArrowUpDown } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';





/**
 * AdminPage - Record Results
 *
 * Per GCHL Brand Guide:
 * - Forest green primary with gold accents
 * - Navy dark mode with proper contrast
 * - Card-based layout with proper shadows
 * - Touch-friendly button sizing
 */
const AdminPage = () => {
  const [events, setEvents] = useState([]);
  const [players, setPlayers] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [pointsConfig, setPointsConfig] = useState({});
  const [isEditingPoints, setIsEditingPoints] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const drawerRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [feedbackMessage, setFeedbackMessage] = useState(null);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [remainingPot, setRemainingPot] = useState(0);
  const [ctpPot, setCtpPot] = useState(0);
  const [skinPot, setSkinPot] = useState(0);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isEditAllMode, setIsEditAllMode] = useState(false);
  const queryParams = new URLSearchParams(location.search);
  const eventId = queryParams.get("eventId"); // Get the eventId from the query string

  // Initialize dark mode from localStorage/system preference
  useEffect(() => {
    const stored = localStorage.getItem('theme');
    if (stored) {
      setIsDarkMode(stored === 'dark');
    } else if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setIsDarkMode(true);
    }
  }, []);

  // Sync dark mode to document
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  const handleOpenSubmitModal = () => {
    setIsSubmitModalOpen(true);
  };

  const handleCloseSubmitModal = () => {
    setIsSubmitModalOpen(false);
  };





  useEffect(() => {
    let isMounted = true;
    const fetchPotValues = async () => {
      if (selectedEvent?.id) {
        try {
          const response = await apiClient.get(`/admin/events/${selectedEvent.id}/pots`);
          const { remainingPot, remainingSkinPot, remainingCtpPot } = response.data;

          if (isMounted) {
            setRemainingPot(remainingPot);
            setSkinPot(remainingSkinPot);
            setCtpPot(remainingCtpPot);
          }
        } catch (error) {
          // Silently handle pot values fetch error
        }
      }
    };

    fetchPotValues();
  }, [selectedEvent]);




  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [eventsRes, pointsConfigRes, allPlayersRes] = await Promise.all([
          apiClient.get('/admin/events?type=upcoming'),
          apiClient.get('/admin/points/config'),
          apiClient.get('/players'),
        ]);

        // Handle new API format that returns { events: [...], meta: {...} }
        const eventsData = eventsRes.data?.events || eventsRes.data || [];
        setEvents(
          eventsData.map((event) => ({
            ...event,
            displayName: `${event.date} - ${event.course_name}`,
          }))
        );

        setPointsConfig(
          pointsConfigRes.data.reduce((acc, item) => {
            acc[item.key] = item.value;
            return acc;
          }, {})
        );

        setAllPlayers(allPlayersRes.data);
      } catch (error) {
        // Silently handle initial data fetch error
      }
    };

    fetchInitialData();
  }, []);

  const memoizedAllPlayers = useMemo(() => allPlayers, [allPlayers]);

  const memoizedPlayers = useMemo(() => {
    return [...players].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [players]);


  const handleCloseEvent = async () => {
    if (!selectedEvent) {
      alert("No event selected.");
      return;
    }

    try {
      await handleSavePlayer();

      await apiClient.post(`/admin/events/${selectedEvent.id}/close`, {});

      setSelectedEvent((prev) => ({
        ...prev,
        isClosed: true,
      }));

      Swal.fire({
        icon: 'success',
        title: 'Event Closed!',
        text: 'The event has recorded successfully.',
        confirmButtonText: 'Great!',
      }).then(() => {
        navigate('/events');
      });

    } catch (error) {
      setFeedbackMessage({
        type: "error",
        text: `Failed to close event. Please try again or contact Jason!`,
      });
    } finally {
      setIsSubmitModalOpen(false);
    }
  };
  const handleRainOutClose = async () => {
    if (!selectedEvent) {
      alert("No event selected.");
      return;
    }

    // Show confirmation dialog explaining what happens
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Close Event as Rainout?',
      html: `
        <p>This will close the event due to rain without recording:</p>
        <ul style="text-align: left; margin: 10px 0;">
          <li>• No scores or points</li>
          <li>• No quota updates</li>
          <li>• No money distribution</li>
        </ul>
        <p>Player quotas will remain unchanged.</p>
      `,
      showCancelButton: true,
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Rainout',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await apiClient.post(`/admin/events/${selectedEvent.id}/close-rainout`, {});

      setSelectedEvent((prev) => ({
        ...prev,
        isClosed: true,
        rainedOut: true,
      }));

      Swal.fire({
        icon: 'success',
        title: 'Event Closed - Rainout!',
        text: 'The event has been closed due to rain. No scores or quotas were recorded.',
        confirmButtonText: 'OK',
      }).then(() => {
        navigate('/events');
      });

    } catch (error) {
      setFeedbackMessage({
        type: "error",
        text: `Failed to close rain-out event. Please try again or contact Jason!`,
      });
    } finally {
      setIsSubmitModalOpen(false);
    }
  };


  const syncPlayersToDatabase = async (players, eventId) => {
    if (!players.length) return; // No updates needed

    try {
      await apiClient.put(`/admin/events/${eventId}/players`, { players });
    } catch (error) {
      // Log the error for debugging
      console.error('Error syncing players to database:', error);
      // Re-throw the error so it can be handled by the calling function
      throw error;
    }
  };

  const handleEventChange = useCallback(
    async (eventId) => {
      const selected = events.find((e) => e.id === eventId);
      if (!selected) {
        return;
      }

      try {
        const eventResponse = await apiClient.get(`/admin/events/${eventId}`);

        const { players = [], details } = eventResponse.data;

        if (Array.isArray(players)) {
          const updatedPlayers = players.map((player) => {
            const calculatedQuota = calculateQuota(player.current_quota ?? 0, player.score ?? 0);


            return {
              player_id: player.player_id,
              name: player.name || "N/A",
              image: player.image_path || null,
              current_quota: player.current_quota ?? 0,
              score: player.score ?? 0,
              calculated_quota: calculatedQuota, // ✅ Ensure it's always set
              rank: player.rank || null,
              ctps: player.ctps || 0,
              skins: player.skins || 0,
              money_won: player.money_won || 0,
              total_points: player.total_points || 0,
              isEditing: false,
              manualMoneyOverride: false,
              new_player: player.new_player || false,
              events_played: player.events_played || 0,
              season_paid: player.season_paid,
            };
          });


          setPlayers((prevPlayers) => {


            return prevPlayers.length > 0 ? prevPlayers.map((player) => {
              const newPlayer = updatedPlayers.find((p) => p.player_id === player.player_id);
              return newPlayer ? { ...player, ...newPlayer } : player;
            }) : updatedPlayers;
          });



        }
        setSelectedEvent({
          id: details.id,
          date: details.date,
          courseName: details.course_name,
          isMajor: details.is_major,
          isFedupEligible: details.fedup_eligible
        });
      } catch (error) {
        // Silently handle event data fetch error to avoid console noise
      }
    },
    [events]
  );


  useEffect(() => {
    if (eventId && players.length === 0) { // ✅ Prevents redundant calls
      handleEventChange(Number(eventId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, players.length]);



  const handleCancelEdit = (playerId) => {
    setPlayers((prevPlayers) =>
      prevPlayers.map((player) => {
        if (player.player_id === playerId) {
          if (!player.backup) {
            return { ...player, isEditing: false }; // Exit edit mode gracefully
          }

          return {
            ...player.backup, // Restore all fields from the backup
            isEditing: false, // Exit edit mode
            backup: undefined, // Clear the backup after restoring
          };
        }
        return player; // Return other players unchanged
      })
    );
  };


  const handleSavePlayer = async () => {
    try {
      // Prepare all players for syncing
      const playersToSync = players.map((player) => ({
        player_id: player.player_id,
        ctps: player.ctps || 0,
        current_quota: player.current_quota,
        calculated_quota: calculateQuota(player.current_quota, player.score),
        skins: player.skins || 0,
        money_won: player.money_won || 0,
        rank: player.rank || null,
        total_points: player.total_points || 0,
        score: player.score || 0,
      }));

      // Sync all players to the backend
      await syncPlayersToDatabase(playersToSync, selectedEvent.id);

      // Update local state to exit edit mode
      setPlayers((prevPlayers) =>
        prevPlayers.map((player) => ({
          ...player,
          isEditing: false, // Exit edit mode
        }))
      );

      setFeedbackMessage({
        type: "success",
        text: "All players updated successfully!",
      });
    } catch (error) {
      setFeedbackMessage({
        type: "error",
        text: "Failed to save players. Please try again or contact Jason!",
      });
      // Re-throw the error to prevent the close event from continuing
      throw error;
    }
  };



  const handleDeletePlayer = async (playerId) => {
    const confirmDelete = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action will remove the player from the event.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete!',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
    });

    // If the user cancels, exit the function
    if (!confirmDelete.isConfirmed) return;

    try {
      // Make the API call to delete the player
      await apiClient.delete(`/admin/events/${selectedEvent.id}/players/${playerId}`);

      // Remove player from state and recalculate pots
      setPlayers((prevPlayers) => {
        const updatedPlayers = prevPlayers.filter(
          (player) => player.player_id !== playerId
        );

        // Recalculate pots and money won
        const {
          updatedPlayers: playersWithMoney,
          remainingPot,
          remainingSkinPot,
          remainingCtpPot,
        } = calculateMoneyWonAndPot(updatedPlayers, updatedPlayers.length);

        // Update pots
        setRemainingPot(remainingPot);
        setSkinPot(remainingSkinPot);
        setCtpPot(remainingCtpPot);

        return playersWithMoney;
      });

      // Provide success feedback
      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: `The player was removed successfully.`,
        confirmButtonColor: '#3085d6',
      });
    } catch (error) {

      // Provide error feedback
      Swal.fire({
        icon: 'error',
        title: 'Failed!',
        text: 'Failed to delete player data. Please try again or contact Jason!',
        confirmButtonColor: '#3085d6',
      });
    }
  };


  const toggleEditMode = useCallback(
    (playerId) => {
      setPlayers((prevPlayers) =>
        prevPlayers.map((player) =>
          player.player_id === playerId
            ? {
              ...player,
              isEditing: !player.isEditing,
              // Add backup only when entering edit mode
              backup: player.isEditing
                ? undefined // Clear backup when exiting edit mode
                : { ...player, remainingPot, skinPot, ctpPot },
            }
            : player
        )
      );
    },
    [remainingPot, skinPot, ctpPot]
  );




  const handleAddPlayer = () => {
    setPlayers((prevPlayers) => [

      ...prevPlayers,
      {

        // player_id: `temp-${Date.now()}`,
        player_id: null,
        name: "",
        ctps: 0,
        score: null,
        skins: 0,
        money_won: 0,
        total_points: 0,
        rank: null,
        current_quota: 0,
        isEditing: true,
        new_player: false,
        events_played: 0,
        season_paid: false,
      },

    ]);
  };

  const handlePlayerChange = useCallback(
    (playerId, updates) => {

      if (!playerId && !updates.player_id) {
        return;
      }

      setPlayers((prevPlayers) => {

        let playerUpdated = false;

        // Update players by mapping through the array
        let updatedPlayers = prevPlayers.map((player) => {
          // Handle case for blank row update
          if (player.player_id === null && updates.player_id) {
            const selectedPlayer = allPlayers.find((p) => p.id === updates.player_id);

            if (!selectedPlayer) {
              return player;
            }

            if (updates.score !== undefined) {
              const newScore = Number(updates.score) || 0;
              updates.calculated_quota = calculateQuota(player.current_quota, newScore);
            }

            playerUpdated = true;

            return {
              ...player,
              player_id: selectedPlayer.id,
              name: selectedPlayer.name,
              current_quota: selectedPlayer.current_quota || 0,
              new_player: selectedPlayer.new_player,
              events_played: selectedPlayer.events_played,
              score: 0, // Default for new players
              rank: null,
              isEditing: true, // Allow immediate editing
              calculated_quota: calculateQuota(selectedPlayer.current_quota, 0),
              season_paid: selectedPlayer.season_paid,
            };

          }

          // Handle updates for existing players
          if (player.player_id === playerId || (player.player_id === null && updates.player_id)) {
            playerUpdated = true;


            const updatedPlayer = {
              ...player,
              ...updates,
              player_id: updates.player_id || player.player_id, // Replace temp ID if updates.player_id exists
              ...(updates.money_won !== undefined && { manualMoneyOverride: true }),
            };

            // Only recalculate total_points if not manually overridden
            if (!updatedPlayer.manualMoneyOverride) {
              updatedPlayer.total_points = calculateTotalPoints(
                updatedPlayer,
                pointsConfig,
                selectedEvent?.isMajor,
                selectedEvent?.isFedupEligible,
                prevPlayers
              );
            }

            return updatedPlayer;
          }

          return player; // Return unchanged player
        });

        if (!playerUpdated) {
          return prevPlayers;
        }


        // Recalculate total_points for all players
        updatedPlayers = updatedPlayers.map((player) => ({
          ...player,
          total_points: calculateTotalPoints(
            player,
            pointsConfig,
            selectedEvent?.isMajor,
            selectedEvent?.isFedupEligible,
            updatedPlayers // Use the updated players array
          ),
        }));

        // Recalculate pots and return updated state
        const {
          updatedPlayers: playersWithMoney,
          remainingPot,
          remainingSkinPot,
          remainingCtpPot,
        } = calculateMoneyWonAndPot(updatedPlayers, updatedPlayers.length);


        setRemainingPot(remainingPot);
        setSkinPot(remainingSkinPot);
        setCtpPot(remainingCtpPot);

        return playersWithMoney;
      });
    },
    [allPlayers, pointsConfig, selectedEvent?.isMajor, selectedEvent?.isFedupEligible]
  );



  const savePointsConfig = async () => {
    try {
      await apiClient.put('/admin/points/config', pointsConfig);
      setIsEditingPoints(false);
      alert("Points configuration saved successfully!");
    } catch (error) {
      alert("Failed to save points configuration.");
    }
  };

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  // Toggle Edit All Mode - enables editing for all players simultaneously
  const toggleEditAllMode = () => {
    const newMode = !isEditAllMode;
    setIsEditAllMode(newMode);

    // When entering edit mode, set all players to editing
    // When exiting, set all players to not editing
    setPlayers((prevPlayers) =>
      prevPlayers.map((player) => ({
        ...player,
        isEditing: newMode,
        backup: newMode ? { ...player, remainingPot, skinPot, ctpPot } : undefined,
      }))
    );
  };

  // Auto-calculate ranks based on scores (higher score = better rank)
  const autoCalculateRanks = () => {
    setPlayers((prevPlayers) => {
      // Filter players with valid scores and sort by score descending
      const playersWithScores = prevPlayers
        .filter((p) => p.score !== null && p.score !== undefined && p.score > 0)
        .sort((a, b) => (b.score || 0) - (a.score || 0));

      // Assign ranks (handling ties)
      let currentRank = 1;
      const rankedPlayers = playersWithScores.map((player, index, arr) => {
        if (index > 0 && player.score === arr[index - 1].score) {
          // Same score as previous player - same rank
          return { ...player, rank: currentRank };
        } else {
          currentRank = index + 1;
          return { ...player, rank: currentRank };
        }
      });

      // Create a map of player_id to rank
      const rankMap = new Map(rankedPlayers.map((p) => [p.player_id, p.rank]));

      // Update all players with their ranks
      return prevPlayers.map((player) => ({
        ...player,
        rank: rankMap.get(player.player_id) || null,
      }));
    });

    setFeedbackMessage({
      type: "success",
      text: "Ranks auto-calculated based on scores.",
    });

    // Clear message after 3 seconds
    setTimeout(() => setFeedbackMessage(null), 3000);
  };

  const handleClickOutsideDrawer = (event) => {
    if (drawerRef.current && !drawerRef.current.contains(event.target)) {
      setIsDrawerOpen(false);
    }
  };
  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };
  const handleHover = debounce((hovering) => setIsHovering(hovering), 200);

  useEffect(() => {
    if (isDrawerOpen) {
      document.addEventListener("mousedown", handleClickOutsideDrawer);
    } else {
      document.removeEventListener("mousedown", handleClickOutsideDrawer);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutsideDrawer);
    };
  }, [isDrawerOpen]);

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-navy-950 flex flex-col transition-colors duration-300">
      <Navbar />

      {/* Header Section - Matches LeaderboardPage style */}
      <div className="bg-white dark:bg-navy-900 border-b border-gray-200 dark:border-navy-800">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-display font-bold text-forest-900 dark:text-white tracking-tight">
                Record Results
              </h1>
              <p className="text-gray-600 dark:text-navy-400 mt-1">
                Admin Dashboard
              </p>
            </div>

            <div className="flex items-center gap-4">
              {/* Points Drawer Toggle */}
              <button
                onClick={toggleDrawer}
                onMouseEnter={() => handleHover(true)}
                onMouseLeave={() => handleHover(false)}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-lg
                  transition-all duration-200
                  ${isDrawerOpen
                    ? 'bg-forest-600 text-white'
                    : 'bg-gray-100 dark:bg-navy-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-navy-700'
                  }
                  border border-gray-200 dark:border-navy-700
                `}
              >
                <span className="text-sm font-medium hidden sm:inline">Points</span>
                {isDrawerOpen ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
              </button>

              {/* Dark Mode Toggle */}
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className="
                  flex items-center gap-2 px-4 py-2 rounded-full
                  bg-gray-100 dark:bg-navy-800
                  text-gray-700 dark:text-gray-200
                  hover:bg-gray-200 dark:hover:bg-navy-700
                  transition-colors duration-200
                  border border-gray-200 dark:border-navy-700
                "
                aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDarkMode ? (
                  <>
                    <Sun className="w-5 h-5 text-gold-400" />
                    <span className="text-sm font-medium hidden sm:inline">Light</span>
                  </>
                ) : (
                  <>
                    <Moon className="w-5 h-5 text-navy-600" />
                    <span className="text-sm font-medium hidden sm:inline">Dark</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Points Preview Tooltip */}
          {isHovering && !isDrawerOpen && (
            <div className="absolute right-4 top-24 z-50 bg-white dark:bg-navy-800 p-4 rounded-xl shadow-lg border border-gray-200 dark:border-navy-700">
              <table className="text-sm text-gray-700 dark:text-gray-200">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-navy-600">
                    <th className="pb-2 text-left font-semibold">Category</th>
                    <th className="pb-2 text-right font-semibold pl-8">Points</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(pointsConfig).map(([key, value]) => (
                    <tr key={key} className="border-b border-gray-100 dark:border-navy-700 last:border-0">
                      <td className="py-1">{labelMapping[key] || key}</td>
                      <td className="py-1 text-right tabular-nums">
                        {selectedEvent?.isMajor
                          ? Math.ceil(value * MAJOR_MULTIPLIER).toFixed(2)
                          : value.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-grow max-w-7xl mx-auto px-4 py-8 w-full">
        {/* Feedback Message */}
        {feedbackMessage && (
          <div
            className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
              feedbackMessage.type === "success"
                ? "bg-seafoam-light/10 text-seafoam-dark dark:bg-seafoam-dark/20 dark:text-seafoam-light border border-seafoam-light/30"
                : "bg-sunset-light/10 text-sunset-dark dark:bg-sunset-dark/20 dark:text-sunset-light border border-sunset-light/30"
            }`}
          >
            {feedbackMessage.type === "success" ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <CloudRain className="w-5 h-5 flex-shrink-0" />
            )}
            {feedbackMessage.text}
          </div>
        )}

        {/* Main Card */}
        <Card className="relative">
          {/* Event Selector */}
          <EventSelector
            events={events}
            selectedEvent={selectedEvent}
            handleEventChange={handleEventChange}
          />

          {/* Major Event Badge */}
          {selectedEvent?.isMajor && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-gold-400/20 text-gold-600 dark:bg-gold-400/30 dark:text-gold-300 text-sm font-semibold rounded-lg border border-gold-400/30">
              <span className="w-2 h-2 bg-gold-400 rounded-full animate-pulse" />
              Major Event - 1.25x Points
            </div>
          )}

          {/* Edit Controls */}
          {selectedEvent && (
            <div className="flex flex-wrap items-center gap-3 mt-4 mb-2">
              {/* Edit All Toggle */}
              <button
                onClick={toggleEditAllMode}
                className={`
                  inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold
                  transition-all duration-200
                  ${isEditAllMode
                    ? 'bg-gold-400 text-navy-900 hover:bg-gold-300'
                    : 'bg-forest-100 dark:bg-forest-900/40 text-forest-700 dark:text-forest-400 hover:bg-forest-200 dark:hover:bg-forest-900/60'
                  }
                `}
              >
                {isEditAllMode ? (
                  <>
                    <Lock className="w-4 h-4" />
                    Lock All
                  </>
                ) : (
                  <>
                    <Edit3 className="w-4 h-4" />
                    Edit All
                  </>
                )}
              </button>

              {/* Auto-Rank Button */}
              <button
                onClick={autoCalculateRanks}
                className="
                  inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold
                  bg-gray-100 dark:bg-navy-800 text-gray-700 dark:text-gray-300
                  hover:bg-gray-200 dark:hover:bg-navy-700
                  transition-colors
                "
              >
                <ArrowUpDown className="w-4 h-4" />
                Auto-Rank
              </button>

              {/* Edit Mode Hint */}
              {isEditAllMode && (
                <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                  Tab through fields to enter data quickly
                </span>
              )}
            </div>
          )}

          {/* Player List */}
          {selectedEvent && (
            <PlayerList
              players={memoizedPlayers}
              toggleEditMode={toggleEditMode}
              handlePlayerChange={handlePlayerChange}
              handleDeletePlayer={handleDeletePlayer}
              handleSavePlayer={handleSavePlayer}
              allPlayers={memoizedAllPlayers}
              selectedEvent={selectedEvent}
              handleAddPlayer={handleAddPlayer}
              handleCancelEdit={handleCancelEdit}
              ctpPot={ctpPot}
              skinPot={skinPot}
              remainingPot={remainingPot}
              isEditAllMode={isEditAllMode}
            />
          )}

          {/* Action Buttons */}
          {selectedEvent && (
            <div className="flex flex-wrap gap-4 mt-6 pt-6 border-t border-gray-200 dark:border-navy-700">
              <Button
                variant="secondary"
                onClick={handleRainOutClose}
                className="flex items-center gap-2"
              >
                <CloudRain className="w-4 h-4" />
                Rainout Close
              </Button>
              <Button
                variant="action"
                onClick={handleOpenSubmitModal}
                className="flex items-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                Close Event
              </Button>
            </div>
          )}
        </Card>

        {/* Points Allocation Drawer */}
        <div
          ref={drawerRef}
          className={`
            fixed top-0 right-0 h-full z-40
            bg-white dark:bg-navy-900
            shadow-2xl border-l border-gray-200 dark:border-navy-700
            transition-transform duration-300 ease-in-out transform
            ${isDrawerOpen ? "translate-x-0" : "translate-x-full"}
          `}
          style={{ width: "380px", overflowY: "auto" }}
        >
          <div className="sticky top-0 bg-forest-900 dark:bg-navy-800 text-white p-4 flex items-center justify-between">
            <h3 className="font-display font-semibold">Points Allocation</h3>
            <button
              onClick={toggleDrawer}
              className="p-2 hover:bg-forest-800 dark:hover:bg-navy-700 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4">
            <PointsAllocation
              pointsConfig={pointsConfig}
              isEditingPoints={isEditingPoints}
              toggleEditPoints={() => setIsEditingPoints(!isEditingPoints)}
              handlePointsChange={(key, value) =>
                setPointsConfig((prevConfig) => ({
                  ...prevConfig,
                  [key]: value,
                }))
              }
              savePointsConfig={savePointsConfig}
              isMajor={selectedEvent?.isMajor}
            />
          </div>
        </div>

        {/* Drawer Overlay */}
        {isDrawerOpen && (
          <div
            className="fixed inset-0 bg-black/30 z-30"
            onClick={toggleDrawer}
          />
        )}
      </main>

      {/* Review Results Modal */}
      <Modal isOpen={isSubmitModalOpen} onClose={handleCloseSubmitModal}>
        <div className="w-full">
          <h2 className="text-2xl font-display font-bold text-forest-900 dark:text-white mb-6">
            Review Results
          </h2>

          <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-navy-700">
            <table className="w-full">
              <thead>
                <tr className="bg-forest-900 dark:bg-navy-800 text-white">
                  <th className="py-3 px-3 text-left font-semibold">Place</th>
                  <th className="py-3 px-3 text-left font-semibold">Name</th>
                  <th className="py-3 px-3 text-center font-semibold">Score</th>
                  <th className="py-3 px-3 text-center font-semibold">CTPs</th>
                  <th className="py-3 px-3 text-center font-semibold">Skins</th>
                  <th className="py-3 px-3 text-center font-semibold">Money</th>
                  <th className="py-3 px-3 text-center font-semibold">Points</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-navy-700">
                {memoizedPlayers.map((player, index) => (
                  <tr
                    key={player.player_id}
                    className={`
                      ${index < 3
                        ? 'bg-gold-50 dark:bg-gold-900/10'
                        : index % 2 === 0
                          ? 'bg-white dark:bg-navy-900'
                          : 'bg-gray-50 dark:bg-navy-800/50'
                      }
                    `}
                  >
                    <td className="py-3 px-3 text-center">
                      <span className={`
                        inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold
                        ${player.rank === 1 ? 'bg-gold-400 text-forest-950' :
                          player.rank === 2 ? 'bg-gray-300 text-gray-800' :
                          player.rank === 3 ? 'bg-amber-600 text-white' :
                          'bg-gray-200 dark:bg-navy-700 text-gray-700 dark:text-gray-300'}
                      `}>
                        {player.rank || '-'}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-left font-medium text-gray-900 dark:text-gray-100">
                      {player.name}
                    </td>
                    <td className="py-3 px-3 text-center tabular-nums text-gray-700 dark:text-gray-300">
                      {player.score}
                    </td>
                    <td className="py-3 px-3 text-center tabular-nums text-gray-700 dark:text-gray-300">
                      {player.ctps}
                    </td>
                    <td className="py-3 px-3 text-center tabular-nums text-gray-700 dark:text-gray-300">
                      {player.skins}
                    </td>
                    <td className="py-3 px-3 text-center tabular-nums text-emerald-600 dark:text-emerald-400 font-medium">
                      ${Number(player.money_won).toFixed(2)}
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span className="inline-flex items-center justify-center px-2 py-1 rounded-lg bg-forest-100 dark:bg-forest-900/40 text-forest-700 dark:text-forest-400 font-bold tabular-nums">
                        {Number(player.total_points).toFixed(0)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end gap-4 mt-6 pt-6 border-t border-gray-200 dark:border-navy-700">
            <Button variant="ghost" onClick={handleCloseSubmitModal}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleCloseEvent}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Finalize & Close Event
            </Button>
          </div>
        </div>
      </Modal>

      <Footer />
    </div>
  );

};

export default AdminPage;
